// src/features/system/developer/components/video-gallery/bandwidthEstimator.ts
// ────────────────────────────────────────────────────────────────────────────
// Bandwidth estimator — the brain behind adaptive prewarm.
//
// Why a custom estimator instead of trusting `navigator.connection`?
//   • `effectiveType` is bucketed (slow-2g / 2g / 3g / 4g) and lags reality
//     by 5–60 s. A user joining a video feed from a fresh tab on Wi-Fi can
//     spend the first 30 s being treated as "3g" until the API catches up.
//   • `downlink` is noisy on iOS/Safari (often clamped at 10 Mbps).
//   • What actually matters is *measured* throughput on the same socket /
//     same origin we're going to hit. That's what every commercial player
//     (Shaka, hls.js, ExoPlayer, AVPlayer ABR) does internally.
//
// Algorithm:
//   • EMA (exponentially-weighted moving average) over per-sample Mbps.
//     α = 0.3 — fast enough to follow a 5G → elevator handover, slow
//     enough to not over-react to a single congested TCP RTT.
//   • Samples come from two sources:
//       1. Range pre-warm fetches (we know bytes + elapsed exactly).
//       2. `<video>` `progress` events (buffered-bytes delta over time).
//   • Floor: NetworkInformation `downlink` (when available) provides a
//     lower bound so we never starve the player while the EMA warms up.
//   • Floor (final): hard 0.5 Mbps so a bogus zero sample can never make
//     us pick the no-op tier.
//
// Hysteresis:
//   • Tier thresholds use a deadband (~15%) so we don't oscillate between
//     two prewarm policies when throughput hovers at the boundary.
//
// Self-healing:
//   • `notifyStall()` triggers an instant demotion: cut the estimate by
//     50% and bias the EMA toward the lower band for the next ~10s.
//     This is the standard "panic mode" pattern from MPEG-DASH players.
//
// Persistence:
//   • Last estimate is written to sessionStorage so a hard reload starts
//     with intelligent defaults instead of a 6 MB prewarm against a 2G
//     connection that was demoted three seconds ago.
//
// Observability:
//   • `localStorage['debug:bandwidth'] = '1'` enables console traces.
// ────────────────────────────────────────────────────────────────────────────

/** Persisted snapshot of the last good estimate (sessionStorage). */
interface PersistedEstimate {
  mbps:     number;
  ts:       number;
  samples:  number;
}

const PERSIST_KEY  = 'video-feed:bandwidth-estimate';
const PERSIST_TTL  = 10 * 60 * 1000;  // 10 min — beyond that, network likely changed
const EMA_ALPHA    = 0.3;
const FLOOR_MBPS   = 0.5;             // never report below this; protects the player
const PANIC_MBPS   = 0.75;             // mode after `notifyStall`
const PANIC_TTL_MS = 10_000;

/** Mutable estimator state — module-singleton (one feed per tab). */
const state = {
  mbps:        0,         // 0 means "not yet measured"
  samples:     0,
  panicUntil:  0,
};

function dbg(...args: unknown[]): void {
  try {
    if (typeof window === 'undefined') return;
    if (window.localStorage?.getItem('debug:bandwidth') !== '1') return;
    // eslint-disable-next-line no-console
    console.debug('[bandwidth]', ...args);
  } catch { /* privacy modes */ }
}

/** Read NetworkInformation.downlink (Mbps) when available. */
function downlinkFloorMbps(): number {
  try {
    const nav = navigator as unknown as {
      connection?: { downlink?: number; effectiveType?: string; saveData?: boolean };
    };
    const c = nav.connection;
    if (!c) return 0;
    if (c.saveData) return 0;
    const d = typeof c.downlink === 'number' ? c.downlink : 0;
    return d > 0 ? d : 0;
  } catch { return 0; }
}

/** Load the persisted estimate if it's still fresh. */
function hydrate(): void {
  try {
    if (state.samples > 0) return;
    if (typeof sessionStorage === 'undefined') return;
    const raw = sessionStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const p = JSON.parse(raw) as PersistedEstimate;
    if (!p || typeof p.mbps !== 'number') return;
    if (Date.now() - p.ts > PERSIST_TTL) return;
    state.mbps    = p.mbps;
    state.samples = Math.min(p.samples ?? 1, 5); // cap so a fresh sample still moves us
    dbg('hydrated', p);
  } catch { /* ignore */ }
}

function persist(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const snap: PersistedEstimate = {
      mbps:    state.mbps,
      ts:      Date.now(),
      samples: state.samples,
    };
    sessionStorage.setItem(PERSIST_KEY, JSON.stringify(snap));
  } catch { /* quota / privacy */ }
}

/**
 * Feed the estimator one observation.
 *
 *   bytes    — actual bytes delivered (Content-Length or progress delta)
 *   elapsedMs — wall-clock time the transfer took (≥ 1 ms)
 *
 * Discards obviously-bogus samples (≤ 0 bytes, ≤ 0 ms, < 64 KB which is
 * too small to be representative). Updates the EMA and persists.
 */
export function recordSample(bytes: number, elapsedMs: number): void {
  if (!Number.isFinite(bytes) || !Number.isFinite(elapsedMs)) return;
  if (bytes < 64 * 1024 || elapsedMs <= 0) return;
  const mbps = (bytes * 8) / (elapsedMs / 1000) / 1_000_000;
  if (!Number.isFinite(mbps) || mbps <= 0) return;

  hydrate();
  if (state.samples === 0) state.mbps = mbps;
  else state.mbps = EMA_ALPHA * mbps + (1 - EMA_ALPHA) * state.mbps;
  state.samples += 1;
  persist();
  dbg('sample', { bytes, elapsedMs, mbps: mbps.toFixed(2), ema: state.mbps.toFixed(2) });
}

/**
 * Signal that the player just stalled (`waiting` / `stalled` event).
 * Forces an immediate downshift so the next prewarm cycle stops
 * starving the active stream.
 */
export function notifyStall(): void {
  hydrate();
  state.panicUntil = Date.now() + PANIC_TTL_MS;
  // Hard-cut the EMA in half so the very next decision is conservative.
  if (state.mbps > 0) state.mbps = Math.max(FLOOR_MBPS, state.mbps * 0.5);
  dbg('panic-demote', { ema: state.mbps.toFixed(2) });
}

/** Current best throughput estimate in Mbps (combines EMA + connection floor). */
export function getThroughputMbps(): number {
  hydrate();
  if (Date.now() < state.panicUntil) return PANIC_MBPS;
  const floor = Math.max(FLOOR_MBPS, downlinkFloorMbps());
  if (state.samples === 0) return floor;
  return Math.max(floor, state.mbps);
}

/** Adaptive prewarm tier — drives bytes-per-slide and lookahead together. */
export interface PrewarmTier {
  bytes:     number;
  lookahead: number;
  mbps:      number;  // throughput that produced this tier (for telemetry)
}

const TIERS = [
  // [maxMbps, bytes, lookahead]
  [1.0,           256 * 1024, 1],   // <1 Mbps — minimum viable head start
  [3.0,           1024 * 1024, 1],
  [8.0,           2 * 1024 * 1024, 2],
  [20.0,          4 * 1024 * 1024, 2],
  [Infinity,      6 * 1024 * 1024, 3],
] as const;

/** Hysteresis: stick with the current tier unless we're well above its ceiling. */
let lastTierIdx = -1;
const HYSTERESIS = 0.15;

/**
 * Decide how many bytes to range-prewarm per upcoming slide AND how many
 * slides ahead to prewarm, given the current throughput estimate.
 *
 * Honours `canAffordPrewarm()`-style gating implicitly via the floor: a
 * save-data / 2G floor of 0 collapses to the bottom tier (256 KB × 1).
 */
export function getPrewarmTier(): PrewarmTier {
  const mbps = getThroughputMbps();
  let idx = TIERS.findIndex(([cap]) => mbps < (cap as number));
  if (idx < 0) idx = TIERS.length - 1;

  // Hysteresis: avoid bouncing between two adjacent tiers when mbps
  // sits within ±15% of a boundary.
  if (lastTierIdx >= 0 && Math.abs(idx - lastTierIdx) === 1) {
    const boundary = TIERS[Math.min(lastTierIdx, idx)][0] as number;
    if (Math.abs(mbps - boundary) / boundary < HYSTERESIS) {
      idx = lastTierIdx;
    }
  }
  lastTierIdx = idx;

  const [, bytes, lookahead] = TIERS[idx];
  return { bytes: bytes as number, lookahead: lookahead as number, mbps };
}

/** Reset state (test hook). */
export function _resetForTesting(): void {
  state.mbps = 0;
  state.samples = 0;
  state.panicUntil = 0;
  lastTierIdx = -1;
}
